"use client";

import Image from "next/image";
import Link from "next/link";
import { type PointerEvent, useEffect, useId, useRef, useState } from "react";
import chevronDown from "@/assets/icons/chevron-down.svg";
import type { AppRoute } from "@/constants/routes";

type PathwaysMenuProps = {
  label: string;
  items: { href: AppRoute; label: string }[];
};

export function PathwaysMenu({ label, items }: PathwaysMenuProps) {
  const [open, setOpen] = useState(false);
  const hoveredRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: globalThis.PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleHover = (event: PointerEvent, hovered: boolean) => {
    if (event.pointerType !== "mouse") return;
    hoveredRef.current = hovered;
    setOpen(hovered);
  };

  return (
    <div
      ref={rootRef}
      onPointerEnter={(event) => handleHover(event, true)}
      onPointerLeave={(event) => handleHover(event, false)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      className="relative"
    >
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen(hoveredRef.current || !open)}
        className="flex items-center gap-1 text-slate-900"
      >
        {label}
        <Image src={chevronDown} alt="" />
      </button>
      {/* The top padding bridges the gap below the trigger, so the pointer can reach the menu without closing it. */}
      <div id={menuId} hidden={!open} className="absolute top-full -left-2.25 z-50 pt-(--site-dropdown-offset)">
        <ul className="w-(--site-dropdown-width) overflow-hidden rounded-dropdown bg-white py-1 shadow-dropdown">
          {items.map((item) => (
            <li key={item.href}>
              <Link
                href={item.href}
                onClick={() => setOpen(false)}
                className="flex min-h-12 items-center px-4 py-2.5 text-menu text-black hover:bg-lavender-100 hover:font-semibold focus-visible:bg-lavender-100 focus-visible:font-semibold"
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
